import { getBeneficiaryRepository, getFiscalQuotaRepository } from "@/infrastructure/di";
import type {
  CreateBeneficiaryResponse,
  CreateFiscalQuotaRequest,
  UpdateFiscalQuotaRequest,
  BeneficiaryUpdateRequest,
  LegalRepUpdateRequest,
} from "@/domain";
import type { IBeneficiaryRepository } from "@/application/ports/repositories/IBeneficiaryRepository";
import type { BeneficiaryStatus, FiscalQuotaState } from "@/domain/beneficiary";
import { InvalidationEntity } from "../entityDependencies";
import { useCreateMutation } from "../createMutation";
import { queryKeys } from "../queryKeys";
import { beneficiaryCache } from "./cache";
import { fiscalQuotaCache, toFiscalQuotaPatch } from "./quotaCache";

export const useCreateBeneficiary = (orgId: string) =>
  useCreateMutation<
    CreateBeneficiaryResponse,
    Error,
    Parameters<IBeneficiaryRepository["createBeneficiary"]>[0]
  >({
    mutationFn: (data) => getBeneficiaryRepository().createBeneficiary(data),
    cache: (c) => c.create(beneficiaryCache.at(orgId)),
    errorLogMessage: "Failed to create beneficiary:",
  });

export const useCreateFiscalQuota = (orgId: string) =>
  useCreateMutation({
    mutationFn: ({
      beneficiaryUuid,
      data,
    }: {
      beneficiaryUuid: string;
      data: CreateFiscalQuotaRequest;
    }) => getBeneficiaryRepository().createFiscalQuota(beneficiaryUuid, data),
    cache: (c) => c.create(fiscalQuotaCache.at(orgId)),
    errorLogMessage: "Failed to create fiscal quota:",
  });

/**
 * Edits of one quota run in order: each paints before the server answers and
 * the next one is built from that painted state, so quick successive edits
 * never overwrite each other.
 */
export const useUpdateFiscalQuota = (orgId: string, beneficiaryUuid: string, quotaUuid: string) =>
  useCreateMutation({
    mutationFn: (data: UpdateFiscalQuotaRequest) =>
      getBeneficiaryRepository().updateFiscalQuota(beneficiaryUuid, quotaUuid, data),
    cache: (c) =>
      c.patch(fiscalQuotaCache.at(orgId), {
        id: () => quotaUuid,
        optimistic: toFiscalQuotaPatch,
        fromResponse: (quota) => quota,
        scope: quotaUuid,
        keys: () => [
          queryKeys.beneficiaries.fiscalQuotaConsolidationPreview(
            orgId,
            beneficiaryUuid,
            quotaUuid,
          ),
          queryKeys.beneficiaries.fiscalQuotaReports(orgId, quotaUuid),
        ],
      }),
    errorLogMessage: "Failed to update fiscal quota:",
  });

/** Moves any quota of the organization to a phase, from the rows of a list. */
export const useSetFiscalQuotaState = (orgId: string) =>
  useCreateMutation({
    mutationFn: ({
      beneficiaryUuid,
      quotaUuid,
      state,
    }: {
      beneficiaryUuid: string;
      quotaUuid: string;
      state: FiscalQuotaState;
    }) => getBeneficiaryRepository().updateFiscalQuota(beneficiaryUuid, quotaUuid, { state }),
    cache: (c) =>
      c.patch(fiscalQuotaCache.at(orgId), {
        id: ({ quotaUuid }) => quotaUuid,
        optimistic: ({ state }) => toFiscalQuotaPatch({ state }),
        fromResponse: (quota) => quota,
      }),
    errorLogMessage: "Failed to change the fiscal quota state:",
  });

export const useDeleteFiscalQuota = (orgId: string) =>
  useCreateMutation({
    mutationFn: ({ beneficiaryUuid, quotaUuid }: { beneficiaryUuid: string; quotaUuid: string }) =>
      getBeneficiaryRepository().deleteFiscalQuota(beneficiaryUuid, quotaUuid),
    cache: (c) => c.remove(fiscalQuotaCache.at(orgId), { id: ({ quotaUuid }) => quotaUuid }),
    errorLogMessage: "Failed to delete fiscal quota:",
  });

export const useBulkDeleteFiscalQuotas = (orgId: string) =>
  useCreateMutation({
    mutationFn: (quotaUuids: readonly string[]) =>
      getFiscalQuotaRepository().bulkDeleteFiscalQuotas(quotaUuids),
    cache: (c) =>
      c.bulk(fiscalQuotaCache.at(orgId), {
        action: "remove",
        ids: (quotaUuids) => quotaUuids,
        result: (result) => result,
      }),
    errorLogMessage: "Failed to delete fiscal quotas in bulk:",
  });

export const useSetBeneficiaryStatus = (orgId: string) =>
  useCreateMutation({
    mutationFn: ({
      beneficiaryUuid,
      status,
    }: {
      beneficiaryUuid: string;
      status: BeneficiaryStatus;
    }) => getBeneficiaryRepository().setBeneficiaryStatus(beneficiaryUuid, status),
    cache: (c) =>
      c.patch(beneficiaryCache.at(orgId), {
        id: ({ beneficiaryUuid }) => beneficiaryUuid,
        optimistic: ({ status }) => ({ status }),
        fromResponse: (beneficiary) => beneficiary,
        scope: "update",
      }),
    errorLogMessage: "Failed to change beneficiary status:",
  });

export const useDeleteBeneficiary = (orgId: string) =>
  useCreateMutation({
    mutationFn: (beneficiaryUuid: string) =>
      getBeneficiaryRepository().deleteBeneficiary(beneficiaryUuid),
    cache: (c) =>
      c.remove(beneficiaryCache.at(orgId), { id: (beneficiaryUuid) => beneficiaryUuid }),
    errorLogMessage: "Failed to delete beneficiary:",
  });

export const useBulkUpdateBeneficiaries = (orgId: string) =>
  useCreateMutation({
    mutationFn: ({ uuids, data }: { uuids: readonly string[]; data: BeneficiaryUpdateRequest }) =>
      getBeneficiaryRepository().bulkUpdateBeneficiaries(uuids, data),
    cache: (c) =>
      c.bulk(beneficiaryCache.at(orgId), {
        action: "patch",
        ids: ({ uuids }) => uuids,
        optimistic: ({ data }) => data,
        result: (result) => result,
      }),
    errorLogMessage: "Failed to update beneficiaries in bulk:",
  });

export const useBulkDeleteBeneficiaries = (orgId: string) =>
  useCreateMutation({
    mutationFn: (uuids: readonly string[]) =>
      getBeneficiaryRepository().bulkDeleteBeneficiaries(uuids),
    cache: (c) =>
      c.bulk(beneficiaryCache.at(orgId), {
        action: "remove",
        ids: (uuids) => uuids,
        result: (result) => result,
      }),
    errorLogMessage: "Failed to delete beneficiaries in bulk:",
  });

export const useUpdateBeneficiary = (orgId: string) =>
  useCreateMutation({
    mutationFn: ({
      beneficiaryUuid,
      data,
    }: {
      beneficiaryUuid: string;
      data: BeneficiaryUpdateRequest;
    }) => getBeneficiaryRepository().updateBeneficiary(beneficiaryUuid, data),
    cache: (c) =>
      c.patch(beneficiaryCache.at(orgId), {
        id: ({ beneficiaryUuid }) => beneficiaryUuid,
        optimistic: ({ data }) => data,
        fromResponse: (beneficiary) => beneficiary,
        scope: "update",
      }),
    errorLogMessage: "Failed to update beneficiary:",
  });

export interface LegalRepCreatePayload {
  first_name: string;
  last_name: string;
  dni: string;
  position?: string | null;
  phone?: string | null;
  email?: string | null;
  is_primary?: boolean;
  is_signer?: boolean;
}

export const useSaveBeneficiaryProfile = (orgId: string) =>
  useCreateMutation<
    void,
    Error,
    {
      beneficiaryUuid: string;
      beneficiaryChanges: BeneficiaryUpdateRequest | null;
      legalRepChanges: Array<{
        uuid: string;
        changes: LegalRepUpdateRequest;
      }>;
      createdReps?: LegalRepCreatePayload[];
      deletedRepUuids?: string[];
    }
  >({
    mutationFn: async ({
      beneficiaryUuid,
      beneficiaryChanges,
      legalRepChanges,
      createdReps = [],
      deletedRepUuids = [],
    }) => {
      const repo = getBeneficiaryRepository();
      if (beneficiaryChanges) {
        await repo.updateBeneficiary(beneficiaryUuid, beneficiaryChanges);
      }
      for (const representativeUuid of deletedRepUuids) {
        await repo.deleteLegalRepresentative(beneficiaryUuid, representativeUuid);
      }
      for (const rep of legalRepChanges) {
        await repo.updateLegalRepresentative(beneficiaryUuid, rep.uuid, rep.changes);
      }
      for (const rep of createdReps) {
        await repo.createLegalRepresentative(beneficiaryUuid, rep);
      }
    },
    cache: (c) =>
      c.invalidate({
        orgId,
        entities: [InvalidationEntity.BENEFICIARY],
        facets: ["lists", "details", "aggregates"],
      }),
    errorLogMessage: "Failed to save beneficiary profile:",
  });

export {
  useSaveFiscalQuota,
  useCreateFilmProduction,
  useAddQuotaProjects,
} from "./quotaWorkflowMutations";
